import { computed, inject, Injectable, Signal, signal } from '@angular/core'
import { httpResource, HttpErrorResponse, HttpResourceRef } from '@angular/common/http'
import {
   IAccount,
   IPaymentInstrumentSubmission,
   ISubscription,
   ISubscriptionAuthorization,
   ISubscriptionConversionOption,
   ISubscriptionPaymentTransaction,
   ISubscriptionUpdateSubmission
} from '@appShared/interfaces/[Model-based]'
import { AccountsService, MockDataService } from '@appShared/services'
import { BillingFrequency, BillingFrequency_ } from '@appShared/services/lookup/[CodeGen]'
import { setErrorMessage } from '@appShared/utils/error-message'

/** Outcome of a store mutation, including any API error to surface. */
export interface IMutationResult {
   ok: boolean
   error?: string
}

/**
 * Feature-scoped store for the Account Detail screen.
 *
 * Lifetime: provided at the AccountDetailComponent route, so a fresh instance
 * is created when the user enters the detail screen and destroyed when they
 * leave. No manual cleanup required.
 *
 * Holds:
 *   - the safeId of the account the user picked from search,
 *   - the account-detail resource keyed off that safeId,
 *   - the three subscription-scoped resources (membership, billing, payments).
 *
 * NOTE: not decorated with `providedIn: 'root'` on purpose. It's registered in
 * the customer-service route's `providers` array.
 */
@Injectable()
export class AccountDetailStore {
   private readonly _mockDataService = inject(MockDataService)
   private readonly _accountsService = inject(AccountsService)

   private readonly _accountsApi = '/api/core/accounts'

   /* Selected account */
   public readonly safeId = signal<string | null>(null)

   public readonly account = httpResource<IAccount | null>(() =>
      this.safeId()
         ? `${this._accountsApi}/${this.safeId()}`
         : undefined,
      {
         defaultValue: null as IAccount | null
      }
   )
   public readonly accountIsLoading = computed(() => this.account.isLoading())
   public readonly accountError = computed(() => this.account.error() as HttpErrorResponse | undefined)
   public readonly accountErrorMessage = computed(() =>
      this.accountError() ? setErrorMessage(this.accountError(), 'account') : null
   )

   public readonly actionableSubscription = computed<ISubscription | undefined>(() =>
      this.account.value()?.info.actionableSubscription
   )

   /* Billing frequency comes from the subscription's product. */
   public readonly billingFrequencyDescription = computed(() =>
      BillingFrequency.toDomainModel(this.actionableSubscription()?.product?.billingFrequencyCode)?.description ?? ''
   )
   /* Partner billed memberships (e.g. CEA, Group Invoice) are invoiced to the partner
      organization, so there is no payment method or plan to administer here. */
   public readonly subscriptionIsPartnerBilled = computed(() =>
      this.actionableSubscription()?.product?.billingFrequencyCode === BillingFrequency_.PartnerBilled
   )

   /* Subscription Conversion Options (plan-change options) */
   public readonly subscriptionConversionOptions = computed(() => {
      let actionableSubscription = this.actionableSubscription()
      let conversionOptions = actionableSubscription?.subscriptionConversionOptions

      /* Stamp a display summary on each option, mirroring the account-card:
         "<productDisplayName>: <currency><amount>" (or just the name if no amount).
         Currency comes from the current subscription's payment instrument. */
      const currency = actionableSubscription?.info?.relevantSubscriptionAuthorization
         ?.paymentInstrument?.info?.currencyShortDescription || '$'

      return conversionOptions
         .map((option) => ({
            ...option,
            productSummary: option.subscriptionAuthorizationAmount != null
               ? `${option.productDisplayName}: ${currency}${option.subscriptionAuthorizationAmount}`
               : option.productDisplayName
         } as ISubscriptionConversionOption))
         .sort((a, b) => Number(a.subscriptionAuthorizationAmount) - Number(b.subscriptionAuthorizationAmount))
   })

   public readonly nextPlannedSubscriptionConversionOption = computed(() =>
      this.subscriptionConversionOptions().find((option) => option.isPlanned)
   )

   /* Subscription Authorizations + Payment Transactions (billing history)
      These two resources are created by — and scoped to — the SubscriptionBillingComponent,
      which drives them off its own accountSafeId input rather than the store's safeId.
      The store keeps a private handle to each so its mutation methods (new payment
      instrument / refund) can reload them after a successful change. The component
      owns the value / isLoading / error computeds. */
   private _subscriptionAuthorizationsResource?: HttpResourceRef<ISubscriptionAuthorization[] | null>
   private _subscriptionPaymentTransactionsResource?: HttpResourceRef<ISubscriptionPaymentTransaction[]>

   /**
    * Creates the subscription-authorizations resource for the given account.
    * Call from a component's injection context (a field initializer) so the
    * resource's lifetime is tied to that component. The store retains a reference
    * so `submitPaymentInstrument` can refresh it after a successful add.
    */
   public createSubscriptionAuthorizationsResource(accountSafeId: Signal<string | null>): HttpResourceRef<ISubscriptionAuthorization[] | null> {
      this._subscriptionAuthorizationsResource = httpResource<ISubscriptionAuthorization[] | null>(() =>
         accountSafeId()
            ? `${this._accountsApi}/${accountSafeId()}/subscription-authorizations`
            : undefined,
         {
            defaultValue: [] as ISubscriptionAuthorization[] | null
         }
      )
      return this._subscriptionAuthorizationsResource
   }

   /**
    * Creates the subscription-payment-transactions (billing history) resource for
    * the given account. Same ownership/lifetime contract as the authorizations
    * resource above; the store retains a reference so `refundPaymentTransaction`
    * can refresh it after a successful reversal.
    */
   public createSubscriptionPaymentTransactionsResource(accountSafeId: Signal<string | null>): HttpResourceRef<ISubscriptionPaymentTransaction[]> {
      this._subscriptionPaymentTransactionsResource = httpResource<ISubscriptionPaymentTransaction[]>(() =>
         accountSafeId()
            ? `${this._accountsApi}/${accountSafeId()}/subscription-payment-transactions`
            : undefined,
         {
            defaultValue: [] as ISubscriptionPaymentTransaction[]
         }
      )
      return this._subscriptionPaymentTransactionsResource
   }

   /* Membership mutation (pause / cancel / resume) */
   public readonly membershipIsUpdating = signal<boolean>(false)

   /**
    * Applies a status change (pause / cancel / resume) to the currently
    * selected subscription, then reloads the account so the new status flows
    * back through `actionableSubscription`.
    *
    * On failure the API error is captured and returned so the caller can show
    * it to the rep. (try/catch here is the async analog of rxjs `catchError` —
    * the catch receives the same HttpErrorResponse a `.subscribe` would.)
    */
   public async updateSubscription(submission: ISubscriptionUpdateSubmission): Promise<IMutationResult> {
      if (!submission?.subscriptionId) {
         return { ok: false, error: 'Missing subscription id.' }
      }

      this.membershipIsUpdating.set(true)
      try {
         await this._accountsService.updateSubscription(submission)
         // re-fetch the selected account; actionableSubscription recomputes from it
         this.account.reload()
         return { ok: true }
      } catch (error) {
         return { ok: false, error: setErrorMessage(error, 'membership') }
      } finally {
         this.membershipIsUpdating.set(false)
      }
   }

   /* Payment transaction refund (reversal) */
   public readonly refundIsSubmitting = signal<boolean>(false)

   /**
    * Submits a reversal (refund) for a payment transaction, then reloads the
    * billing history so the transaction reflects its new status. Pulls the
    * account safeId from the store and the transaction safeId from the row.
    */
   public async refundPaymentTransaction(transaction: ISubscriptionPaymentTransaction): Promise<IMutationResult> {
      const accountSafeId = this.safeId()
      const subscriptionPaymentTransactionSafeId = transaction?.info?.safeId
      if (!accountSafeId || !subscriptionPaymentTransactionSafeId) {
         return { ok: false, error: 'Missing account or payment transaction id.' }
      }

      this.refundIsSubmitting.set(true)
      try {
         await this._accountsService.submitPaymentTransactionReversal(accountSafeId, subscriptionPaymentTransactionSafeId)
         // re-fetch account in case the refunded transaction affected the account status
         this.account.reload()
         // re-fetch billing history so the reversed transaction's status updates
         this._subscriptionPaymentTransactionsResource?.reload()
         return { ok: true }
      } catch (error) {
         return { ok: false, error: setErrorMessage(error, 'refund') }
      } finally {
         this.refundIsSubmitting.set(false)
      }
   }

   /* New payment instrument (EFT / card) submission */
   public readonly paymentInstrumentIsSubmitting = signal<boolean>(false)

   /**
    * Submits a new payment-instrument + billing address for the selected
    * account, then reloads the authorizations. Stamps walletContactId from the
    * account owner's profile.
    */
   public async submitPaymentInstrument(submission: IPaymentInstrumentSubmission): Promise<IMutationResult> {
      if (!submission) {
         return { ok: false, error: 'Missing payment information.' }
      }

      const accountSafeId = this.safeId()
      submission.walletContactId = this.actionableSubscription()?.info?.accountOwnerProfile?.contactId

      this.paymentInstrumentIsSubmitting.set(true)
      try {
         await this._accountsService.updateSubscriptionAuthorizations(accountSafeId, submission)
         // re-retch account in case the new payment instrument affected the account status
         this.account.reload() 
         // re-fetch the payment method so the new instrument shows
         this._subscriptionAuthorizationsResource?.reload()
         return { ok: true }
      } catch (error) {
         return { ok: false, error: setErrorMessage(error, 'payment method') }
      } finally {
         this.paymentInstrumentIsSubmitting.set(false)
      }
   }

   /* Plan change (set the planned / next-renewal conversion option) */
   public readonly planChangeIsSubmitting = signal<boolean>(false)

   /**
    * Sets the given conversion option as the subscription's planned (next-renewal)
    * plan, then reloads the account so the change flows back through
    * `actionableSubscription` / `subscriptionConversionOptions`. The conversion
    * option's safeId comes from its own `info.safeId`.
    */
   public async setPlannedConversionOption(option: ISubscriptionConversionOption): Promise<IMutationResult> {
      const accountSafeId = this.safeId()
      const subscriptionSafeId = this.actionableSubscription()?.info?.safeId
      const conversionOptionSafeId = option?.info?.safeId
      if (!accountSafeId || !subscriptionSafeId || !conversionOptionSafeId) {
         return { ok: false, error: 'Missing account, subscription, or plan option id.' }
      }

      this.planChangeIsSubmitting.set(true)
      try {
         await this._accountsService.setSubscriptionConversionOptionPlanned(accountSafeId, subscriptionSafeId, conversionOptionSafeId)
         // re-fetch the selected account; actionableSubscription + options recompute from it
         this.account.reload()
         return { ok: true }
      } catch (error) {
         return { ok: false, error: setErrorMessage(error, 'plan') }
      } finally {
         this.planChangeIsSubmitting.set(false)
      }
   }

   /* Seat limit edit */
   public readonly seatLimitIsUpdating = signal<boolean>(false)

   /**
    * Updates the seat limit on the currently selected subscription, then
    * reloads the account so the new limit flows back through
    * `actionableSubscription`.
    */
   public async updateSeatLimit(seatLimit: number): Promise<IMutationResult> {
      const accountSafeId = this.safeId()
      const subscriptionSafeId = this.actionableSubscription()?.info?.safeId
      if (!accountSafeId || !subscriptionSafeId || !seatLimit || seatLimit < 1) {
         return { ok: false, error: 'Missing account, subscription, or a valid seat limit.' }
      }

      this.seatLimitIsUpdating.set(true)
      try {
         await this._accountsService.updateSubscriptionSeatLimit(accountSafeId, subscriptionSafeId, seatLimit)
         // re-fetch the selected account; actionableSubscription recomputes from it
         this.account.reload()
         return { ok: true }
      } catch (error) {
         return { ok: false, error: setErrorMessage(error, 'seat limit') }
      } finally {
         this.seatLimitIsUpdating.set(false)
      }
   }
}
